/**
 * Окно входа поверх страницы: /login, /register, «Подтвердите email» и восстановление пароля, открытые изнутри сайта
 * (useOpenAuth), App рисует оверлеем — страница под ним не размонтируется. Владелец 11.10: гость с рекламной ссылки
 * на тренд видит карточку в виджете, а «Регистрация» не уводит с неё — тренд остаётся за окном, после входа окно
 * закрывается, и виджет уже готов к генерации. Формы те же, что на полных страницах (капча, отпечаток, реферальный
 * код, метки рекламы, двухфакторный вход, возврат по ссылке из письма и после Telegram). Прямой заход и F5 на
 * /login, /register — по-прежнему полная страница.
 *
 * Бэкдроп-blur живёт на ::before (см. _auth.scss), как у окна тарифов: backdrop-filter на самом контейнере сделал
 * бы его containing block для fixed-потомков.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, type ReactNode } from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Login from '../pages/Login'
import Register from '../pages/Register'
import ForgotPassword from '../pages/ForgotPassword'
import { useAuth } from '../context/AuthContext'
import { useAdoptOtherTabLogin } from '../hooks/useAuthModal'
import { useScrollLock } from '../hooks/useScrollLock'
import { pageHit } from '../utils/ym'
import { backgroundOf } from '../utils/routeModal'

const VerifyPending = lazy(() => import('../pages/VerifyPending'))

export default function AuthRouteModal() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const location = useLocation()
	const navigationType = useNavigationType()
	const { user } = useAuth()
	const dialogRef = useRef<HTMLDivElement>(null)
	// Закрытие по клику мимо окна — только если и нажатие было мимо: выделение текста в поле, отпущенное за краем
	// карточки, окно не закрывает.
	const downOnBackdrop = useRef(false)
	const closing = useRef(false)
	const close = useCallback(() => navigate(-1), [navigate])

	// Вошёл — окно своё дело сделало: шаг назад на страницу под ним, ровно один раз. Так закрывает и вход здесь
	// (Login → useAuthNav.done), и вход в другой вкладке, и «Вперёд» браузера после входа (окно не открывается
	// вошедшему снова).
	useEffect(() => {
		if (!user || closing.current) return
		closing.current = true
		close()
	}, [user, close])

	// Прокрутка страницы заперта общим замком (прокручивается html, не body): замок на body схлопывал страницу,
	// и под окном она прыгала наверх. Вошедшему окно не показывается — и замка нет.
	useScrollLock(!user)

	// Фокус — в окне (клавиатура и скринридер сразу в нём). Метрика: раньше «Регистрация» загружала страницу заново,
	// и просмотр /register считался сам; окно открывается без перезагрузки — просмотр шлём явно, с адресом страницы
	// под окном как referer. Только на открытие кнопкой (PUSH): F5 при открытом окне счётчик засчитывает сам, а
	// переходы внутри окна просмотрами не были и раньше.
	useEffect(() => {
		// Вошедшему окно не показывается («Вперёд» после входа) — и просмотра в Метрику нет
		if (user) return
		dialogRef.current?.focus({ preventScroll: true })
		const page = backgroundOf(location)
		const origin = window.location.origin
		if (navigationType === 'PUSH') {
			pageHit(origin + location.pathname + location.search, page ? origin + page.pathname + page.search : undefined)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- один раз на открытие окна
	}, [])

	// Переход внутри окна (вход ↔ регистрация) снимает фокус вместе с нажатой ссылкой — возвращаем его в окно
	useEffect(() => {
		const dialog = dialogRef.current
		if (dialog && !dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true })
	}, [location.pathname])

	// Escape закрывает только окно: оно верхний слой, и шторка или окно страницы под ним (свои Escape на document)
	// закрываться вместе с ним не должны — перехват на window до всех остальных.
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Escape') return
			e.stopPropagation()
			close()
		}
		window.addEventListener('keydown', onKey, true)
		return () => window.removeEventListener('keydown', onKey, true)
	}, [close])

	// Почту подтвердили в другой вкладке — вход подхватывается и здесь: окно закрывается, гость на тренде становится
	// вошедшим без перезагрузки.
	useAdoptOtherTabLogin()

	const titles: Record<string, string> = {
		'/login': t('auth.login.title'),
		'/register': t('auth.register.title'),
		'/verify-pending': t('auth.verifyPending.title'),
		'/forgot-password': t('auth.forgot.title'),
	}
	// Вошедшему окно не рисуем: шаг назад уже идёт, страница под окном видна сразу
	if (user) return null

	let page: ReactNode = null
	if (location.pathname === '/login') page = <Login />
	else if (location.pathname === '/register') page = <Register />
	else if (location.pathname === '/verify-pending') page = <VerifyPending />
	else if (location.pathname === '/forgot-password') page = <ForgotPassword />

	return (
		<div
			className="auth-route-modal"
			onPointerDown={(e) => {
				downOnBackdrop.current = e.target === e.currentTarget
			}}
			onClick={(e) => {
				if (downOnBackdrop.current && e.target === e.currentTarget) close()
			}}
		>
			<div
				ref={dialogRef}
				className="auth-route-modal__dialog"
				role="dialog"
				aria-modal="true"
				aria-label={titles[location.pathname]}
				tabIndex={-1}
			>
				<Suspense fallback={null}>{page}</Suspense>
				<button type="button" className="auth-route-modal__close" aria-label="Закрыть" onClick={close}>
					<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
						<line x1="18" y1="6" x2="6" y2="18" />
						<line x1="6" y1="6" x2="18" y2="18" />
					</svg>
				</button>
			</div>
		</div>
	)
}
